// Builds + dispatches a `com.ping.voice` envelope through an MLS
// `Conversation` handle and writes a local-echo row so the sender's
// bubble renders immediately. The shape is:
//
//   { kind: 'com.ping.voice',
//     payload: {
//       mime: 'audio/webm;codecs=opus',
//       durationMs,
//       bytes: <base64>,
//     } }
//
// The receiver-side decoder (shared
// `packages/ui/src/sync/useIncomingMessages.ts`) persists the FULL
// envelope JSON as the row's `body` for `com.ping.voice` kinds;
// the chat `messageVariants` map then routes it to the right
// bubble (here a `VoiceMessageBubble`).

import {
  encodeEnvelopeJsonBytes,
  encodeEnvelopeJsonString,
  type Message,
  mintEventId,
  persistSentMessage as sharedPersistSentMessage,
} from '@ping/ui';

import { log } from '@core/log/log';
import { retrieveIdentity } from '@core/persistence/identity';

import { webConversationsPersister, webMessagesPersister } from './persistenceAdapters';

// Mirror of the SDK's MessageEnvelope return from conv.send. Only the
// fields used for local-echo are typed.
interface SentMessageEnvelope {
  conversation_id: Uint8Array;
  epoch: number | bigint;
  content_hash: Uint8Array;
  hlc: { wall_ms: number | bigint; logical: number };
}

interface SendableConversation {
  send(plaintext: Uint8Array): Promise<SentMessageEnvelope>;
}

export interface VoiceEnvelopePayload {
  readonly mime: string;
  readonly durationMs: number;
  /** Base64-encoded audio bytes. */
  readonly bytes: string;
  /** Optional record-time transcript (Web Speech), shown in the bubble. */
  readonly transcript?: string;
}

export interface VoiceEnvelope {
  readonly kind: 'com.ping.voice';
  readonly payload: VoiceEnvelopePayload;
  /** Thread root content-hash when this note is a reply INTO a thread. */
  readonly parent_id?: string;
}

export interface SendVoiceMessageOptions {
  readonly conv: SendableConversation;
  readonly conversationId: string;
  readonly blob: Blob;
  readonly durationMs: number;
  /** Record-time transcript captured alongside the recording. */
  readonly transcript?: string | undefined;
  /** When set, the note is posted as a reply into this thread root. */
  readonly parentId?: string | undefined;
}

/**
 * Encodes the recorded `Blob` as a `com.ping.voice` envelope, sends
 * it through MLS, and writes the local-echo row so the sender's
 * voice bubble renders immediately. The `body` of the persisted row
 * is the full envelope JSON so the chat surface can compute a
 * `messageVariants` entry without round-tripping through Dexie
 * sidecar tables. Returns the resolved sender content-hash hex on
 * success (mirrors `Message.contentHash`).
 */
/**
 * Server-side caps the MLS envelope at 1 MiB (see
 * `services/message-service/src/envelope/validate.rs::MAX_ENVELOPE_BYTES`).
 * Account for: MLS framing overhead (~few KB), base64 inflation (×1.33),
 * the JSON wrapper, and a safety margin → cap the RAW audio bytes well
 * under the envelope ceiling so the wire-formatted bytes always fit.
 *
 *   raw_max * 1.34 (base64) + ~5 KB (json + mls framing) ≤ 1_048_576
 * → raw_max ≈ 760 KB.
 *
 * Inline base64 audio is a stopgap until the blob-upload path lands
 * (doc spec §2.2 `VoiceBody.blob_ref`). When that ships voice notes
 * become event_id + ref + key only, and this cap goes away.
 */
const MAX_VOICE_RAW_BYTES = 760 * 1024;

/**
 * Thrown by `sendVoiceMessage` when the recorded blob exceeds
 * `MAX_VOICE_RAW_BYTES`. Callers should catch + surface a friendly
 * "Voice note too long" toast rather than letting it bubble.
 */
export class VoiceNoteTooLargeError extends Error {
  readonly rawBytes: number;
  readonly limitBytes: number;
  constructor(rawBytes: number) {
    super(
      `Voice note is too long (${(rawBytes / 1024).toFixed(0)} KB, max ${(MAX_VOICE_RAW_BYTES / 1024).toFixed(0)} KB)`,
    );
    this.name = 'VoiceNoteTooLargeError';
    this.rawBytes = rawBytes;
    this.limitBytes = MAX_VOICE_RAW_BYTES;
  }
}

export async function sendVoiceMessage(opts: SendVoiceMessageOptions): Promise<string> {
  const { conv, conversationId, blob, durationMs, transcript, parentId } = opts;

  if (blob.size > MAX_VOICE_RAW_BYTES) {
    throw new VoiceNoteTooLargeError(blob.size);
  }

  const buffer = await blob.arrayBuffer();
  const base64 = bytesToBase64(new Uint8Array(buffer));
  const trimmedTranscript = transcript?.trim();
  const eventId = mintEventId();
  const encodable = {
    kind: 'com.ping.voice' as const,
    event_id: eventId,
    payload: {
      mime: blob.type !== '' ? blob.type : 'audio/webm',
      durationMs: Math.max(0, Math.round(durationMs)),
      bytes: base64,
      ...(trimmedTranscript !== undefined && trimmedTranscript.length > 0
        ? { transcript: trimmedTranscript }
        : {}),
      // Thread reply → self-identify with `thread: true` so the main timeline
      // folds it out. For non-text kinds parent_id only means a thread reply.
      ...(parentId !== undefined ? { thread: true } : {}),
    },
    // `parent_id` rides on the envelope (§1.1 key 4) so the note threads
    // under its root; receivers rebuild `inReplyTo` from it.
    ...(parentId !== undefined ? { parent_id: parentId } : {}),
  };
  const payloadBody = encodeEnvelopeJsonString(encodable);
  const payloadBytes = encodeEnvelopeJsonBytes(encodable);

  const sentEnvelope = await conv.send(payloadBytes);
  const contentHashHex = bytesToHex(sentEnvelope.content_hash);

  const identity = await retrieveIdentity();
  if (!identity) {
    log.warn({ event: 'voice.persistSent.no_identity' });
    return contentHashHex;
  }

  const epoch =
    typeof sentEnvelope.epoch === 'bigint' ? Number(sentEnvelope.epoch) : sentEnvelope.epoch;
  const sentAt = Date.now();
  const id = eventId;

  await sharedPersistSentMessage({
    messages: webMessagesPersister,
    conversations: webConversationsPersister,
    record: {
      id,
      conversationId,
      epoch,
      serverSeq: sentAt,
      contentHash: contentHashHex,
      senderAccountId: identity.accountId,
      // Persist the full envelope JSON as the body so the chat
      // surface can parse it client-side and dispatch the voice
      // variant. Body is opaque-string in the shared schema.
      body: payloadBody,
      sentAt,
      deliveryState: 'sent',
      // Thread reply → mirror the parent onto the local echo so the
      // sender's own row renders inside the thread immediately.
      ...(parentId !== undefined
        ? { inReplyTo: { messageId: parentId, preview: 'Voice message' } }
        : {}),
    },
  });

  log.debug({
    event: 'voice.send.ok',
    conversationIdPrefix: conversationId.slice(0, 6),
    durationMs,
    sizeBytes: blob.size,
  });

  return contentHashHex;
}

/** Detects whether a persisted `Message.body` is a voice envelope. */
export function isVoiceEnvelopeBody(body: string | undefined): boolean {
  if (body === undefined || body.length === 0) return false;
  if (!body.startsWith('{')) return false;
  return body.includes('"com.ping.voice"');
}

/**
 * Parses a `com.ping.voice` envelope from a persisted message body.
 * Returns `undefined` on any decode failure so the caller can fall
 * back to the plain-text bubble.
 */
export function parseVoiceEnvelope(body: string | undefined): VoiceEnvelopePayload | undefined {
  if (body === undefined) return undefined;
  try {
    const parsed = JSON.parse(body) as Partial<VoiceEnvelope>;
    if (parsed.kind !== 'com.ping.voice') return undefined;
    const p = parsed.payload;
    if (
      p === undefined ||
      typeof p.mime !== 'string' ||
      typeof p.durationMs !== 'number' ||
      typeof p.bytes !== 'string'
    ) {
      return undefined;
    }
    return {
      mime: p.mime,
      durationMs: p.durationMs,
      bytes: p.bytes,
      ...(typeof p.transcript === 'string' && p.transcript.length > 0
        ? { transcript: p.transcript }
        : {}),
    };
  } catch {
    return undefined;
  }
}

/**
 * Lifts a `Message` into a Blob URL suitable for `<audio src=>`. The
 * caller is responsible for revoking the URL when the bubble
 * unmounts. Returns `null` when the body is not a voice envelope.
 */
export function voiceBlobUrlForMessage(message: Message): string | null {
  const payload = parseVoiceEnvelope(message.body);
  if (payload === undefined) return null;
  try {
    const bytes = base64ToBytes(payload.bytes);
    // Copy into a fresh ArrayBuffer so the Blob constructor sees a
    // BlobPart-compatible shape regardless of how the Uint8Array's
    // backing buffer was allocated (lib.dom's `BlobPart` excludes
    // `SharedArrayBuffer`, which can leak in from some TypedArrays
    // under newer DOM lib versions).
    const ab = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(ab).set(bytes);
    const blob = new Blob([ab], { type: payload.mime });
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// base64 helpers — kept local so the module is self-contained.
// `btoa` accepts a binary-string of code points ≤ 0xFF; we synthesise
// one from the byte array. The chunked approach avoids
// `RangeError: Maximum call stack size exceeded` on long buffers.
// ---------------------------------------------------------------------------

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode(...chunk);
  }
  return btoa(binary);
}

function base64ToBytes(b64: string): Uint8Array {
  const binary = atob(b64);
  const len = binary.length;
  const out = new Uint8Array(len);
  for (let i = 0; i < len; i++) {
    out[i] = binary.charCodeAt(i);
  }
  return out;
}

function bytesToHex(b: Uint8Array): string {
  let out = '';
  for (let i = 0; i < b.length; i++) {
    out += (b[i] ?? 0).toString(16).padStart(2, '0');
  }
  return out;
}
